import {FormikErrors} from 'formik';
import {set, toNumber, unset} from 'lodash-es';
import {
  OVRecommendActionEnum,
  ComboFrequencyEnum,
} from '../../../schema-types/globalTypes';
import {CASH_OBJ} from '../../../utils/potsFilter';
import {getIsMinor} from '../useSaveRecommendation/helpers';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {comboToFrequencyMapping} from '../hooks/contributeHooks';
import {newPlanValidator} from '../RecommendsSetUpNew/setUpNewValidator';
import {hasWrapper} from '../CreateRecommend/RecommendSourceDestination/utils';
import FREQUENCY from '../../../constants/Frequency';
import {getComboFrequency} from '../../../hooks/useComboFrequency';

export const validateContributeComboFrequency = (
  values: recommendFieldsType,
  errors: FormikErrors<recommendFieldsType>
) => {
  const frequency =
    comboToFrequencyMapping[
      values.contribute?.comboFrequency ?? ComboFrequencyEnum.SINGLE
    ];
  const start = values.contribute?.contributeFrom?.start;

  if (!frequency) {
    set(errors, 'contribute.comboFrequency', 'Frequency is required');
    return;
  }

  const {hasSingle, hasRecurring} = getComboFrequency(values.contribute?.comboFrequency);
  const contributeFrom = values.contribute?.contributeFrom;

  if (
    hasSingle &&
    !contributeFrom?.singleClientAmount &&
    !contributeFrom?.singleEmployerAmount
  ) {
    set(
      errors,
      'contribute.contributeFrom.singleClientAmount',
      'At least one single amount is required'
    );
  }
  if (
    hasRecurring &&
    !contributeFrom?.recurringClientAmount &&
    !contributeFrom?.recurringEmployerAmount
  ) {
    set(
      errors,
      'contribute.contributeFrom.recurringClientAmount',
      'At least one recurring amount is required'
    );
  }

  if (frequency !== FREQUENCY.SINGLE) {
    if (!start) {
      set(errors, 'contribute.contributeFrom.start', 'Start date is required');
      return;
    }
    const date = new Date(start.valueOf()).getUTCDate();
    if (date !== 1 && date !== 15) {
      set(errors, 'contribute.contributeFrom.start', 'Date must be 1st or 15th');
    }
  }
};

export const contributeValidator = (
  values: recommendFieldsType,
  errors: FormikErrors<recommendFieldsType>
): FormikErrors<any> => {
  if (!values.goal?.id) {
    if (!values.isSppDa) {
      errors.goal = 'Please select a goal';
    }
  }
  const contributeValues = values.contribute;
  const isCash = values.contribute?.contributeFrom?.id === CASH_OBJ.id;

  if (!contributeValues) {
    errors.contribute = 'Contribution seems to be empty';
    return errors;
  }
  if (!contributeValues.contributeFrom) {
    set(errors, 'contribute.contributeFrom', 'Please select an account');
  } else {
    validateContributeComboFrequency(values, errors);
    if (
      !contributeValues.contributeFrom.singleClientAmount &&
      !contributeValues.contributeFrom.recurringClientAmount &&
      !contributeValues.contributeFrom.singleEmployerAmount &&
      !contributeValues.contributeFrom.recurringEmployerAmount
    ) {
      set(errors, 'contribute.contributeFrom', 'At least one amount is required');
      set(
        errors,
        'contribute.contributeFrom.singleClientAmount',
        'At least one amount is required'
      );
      set(
        errors,
        'contribute.contributeFrom.recurringClientAmount',
        'At least one amount is required'
      );
      set(
        errors,
        'contribute.contributeFrom.singleEmployerAmount',
        'At least one amount is required'
      );
      set(
        errors,
        'contribute.contributeFrom.recurringEmployerAmount',
        'At least one amount is required'
      );
    }
    const {hasSingle} = getComboFrequency(values.contribute?.comboFrequency);

    try {
      if (
        toNumber(contributeValues.contributeFrom.singleClientAmount || 0) > 0 ||
        toNumber(contributeValues.contributeFrom.recurringClientAmount || 0) > 0
      ) {
        if (hasSingle && !values.isLisa) {
          if (values.isSppDa) {
            if (!contributeValues.contributeFrom.legacyInitialFee?.feeType) {
              set(
                errors,
                `contribute.contributeFrom.legacyInitialFee.feeType`,
                'Required'
              );
            }
            if (
              contributeValues.contributeFrom.legacyInitialFee?.feeType === '%' &&
              contributeValues.contributeFrom.legacyInitialFee.percentageFee === undefined
            ) {
              set(
                errors,
                `contribute.contributeFrom.legacyInitialFee.percentageFee`,
                'Required'
              );
            }
            if (
              contributeValues.contributeFrom.legacyInitialFee?.feeType === 'GBP' &&
              contributeValues.contributeFrom.legacyInitialFee.fixedFee === undefined
            ) {
              set(
                errors,
                `contribute.contributeFrom.legacyInitialFee.fixedFee`,
                'Required'
              );
            }
            if (
              contributeValues.contributeFrom.legacyInitialFee?.feeType === '%' &&
              contributeValues.contributeFrom.legacyInitialFee.percentageFee === undefined
            ) {
              set(
                errors,
                `contributeValues.contributeFrom.legacyInitialFee.percentageFee`,
                'Required'
              );
            }
          } else if (!contributeValues.contributeFrom.initialFee) {
            set(errors, 'contribute.contributeFrom.initialFee', 'Required');
          }
        }
      }
    } catch (err) {
      // do nothing
    }
    try {
      if (
        toNumber(contributeValues.contributeFrom.singleEmployerAmount || 0) > 0 ||
        toNumber(contributeValues.contributeFrom.recurringEmployerAmount || 0) > 0
      ) {
        if (hasSingle && !values.isLisa) {
          if (values.isSppDa) {
            const legacyEmployersValue =
              contributeValues.contributeFrom.legacyInitialEmployerFee;
            const legacyEmployersKey =
              'contribute.contributeFrom.legacyInitialEmployerFee';
            if (!legacyEmployersValue?.feeType) {
              set(errors, `${legacyEmployersKey}.feeType`, 'Required');
            }
            if (
              legacyEmployersValue?.feeType === '%' &&
              legacyEmployersValue.percentageFee === undefined
            ) {
              set(errors, `${legacyEmployersKey}.percentageFee`, 'Required');
            }
            if (
              legacyEmployersValue?.feeType === 'GBP' &&
              legacyEmployersValue.fixedFee === undefined
            ) {
              set(errors, `${legacyEmployersKey}.fixedFee`, 'Required');
            }
            if (
              legacyEmployersValue?.feeType === '%' &&
              legacyEmployersValue.percentageFee === undefined
            ) {
              set(errors, `${legacyEmployersKey}.percentageFee`, 'Required');
            }
            // For non SPP DAs:
          } else if (!contributeValues.contributeFrom.initialEmployerFee) {
            set(errors, 'contribute.contributeFrom.initialEmployerFee', 'Required');
          } else {
            unset(errors, 'contribute.contributeFrom.initialEmployerFee');
          }
        }
      }
    } catch (err) {
      // do nothing
    }

    // contributions ONLY expect cash contribution which still have source of funds
    // if (isCash) {
    //   validateTextField(
    //     contributeValues.contributeFrom.sourceOfFunds,
    //     'contribute.contributeFrom.sourceOfFunds',
    //     errors
    //   );
    // }
  }
  if (!contributeValues.contributeTo) {
    set(errors, 'contribute.contributeTo', 'Please select an account');
  }
  if (contributeValues.contributeTo === contributeValues.contributeFrom?.id) {
    set(
      errors,
      'contribute.contributeTo',
      'Cannot use the same contribution for both sides'
    );
  }

  const {isMinor} = values;

  if (contributeValues.contributeTo === 'newPlan') {
    newPlanValidator({
      values,
      errors,
      formikKeyPrefix: `contribute.newPlan`,
      context: OVRecommendActionEnum.CONTRIBUTE,
      isSaltusProvider: values.contribute?.destinationValues?.isSaltusProvider,
      isLowesPension: values.isLowesPension,
      isTransactBond: values.isTransactBond,
      isMinor,
    });
  } else if (
    hasWrapper(values.mainPlanType) &&
    values.contribute?.destinationValues?.isSaltusProvider
  ) {
    const subplansSelected = Object.entries(
      contributeValues.destinationValues?.subplans || {}
    ).filter(([id, fields]) => {
      return fields.checked;
    });
    if (!subplansSelected.length) {
      set(errors, 'transfer.destinationAccount', 'At least one subplan must be selected');
    }
  }
  return errors;
};
